import React from "react";
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import LibraryMusicOutlined from "@mui/icons-material/LibraryMusicOutlined";
import UploadArea, { MediaAddTile } from "./UploadArea";
import PickMusicLibraryModal from "./PickMusicLibraryModal";
import { mediaSrc } from "../../../utils/mediaSrc";

const TILE_WIDTH = 104;
const TILE_HEIGHT = 128;

function librarySelectionKey(form) {
  if (form.musicSource !== "library") return "";
  const item = (form.referenceAudioUploads || [])[0];
  return item?.libraryKey || "";
}

function LibraryTrackTile({ label, previewUrl, onRemove, lang }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: TILE_WIDTH,
        height: TILE_HEIGHT,
        borderRadius: 2.5,
        border: "1px dashed",
        borderColor: alpha(theme.palette.text.primary, 0.22),
        bgcolor: alpha(theme.palette.action.hover, 0.03),
        position: "relative",
        overflow: "hidden",
        p: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
      }}
    >
      <IconButton
        size="small"
        onClick={onRemove}
        aria-label={lang?.remove || "Remove"}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          zIndex: 2,
          width: 24,
          height: 24,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
        }}
      >
        <CloseOutlined sx={{ fontSize: 14 }} />
      </IconButton>
      <LibraryMusicOutlined sx={{ fontSize: 28, color: "text.secondary" }} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: "center", lineHeight: 1.2, px: 0.5, wordBreak: "break-word" }}
      >
        {label.length > 32 ? `${label.slice(0, 29)}…` : label}
      </Typography>
      {previewUrl ? (
        <Box component="audio" controls src={previewUrl} sx={{ width: "100%", height: 28, mt: 0.25 }} />
      ) : null}
    </Box>
  );
}

/** Music-to-video: platform library track or file upload. */
export default function MusicAudioSection({ form, onChange, lang }) {
  const set = (patch) => onChange({ ...form, ...patch });
  const musicSource = form.musicSource || "upload";
  const [libraryOpen, setLibraryOpen] = React.useState(false);
  const libraryItem = (form.referenceAudioUploads || [])[0];
  const libraryLabel =
    form.libraryMusicTitle ||
    libraryItem?.name ||
    (form.libraryMusicJobId ? `Song #${form.libraryMusicJobId}` : "");

  const selectLibraryTrack = (row) => {
    const previewUrl = mediaSrc(row.audioUrl) || row.audioUrl;
    set({
      musicSource: "library",
      libraryMusicJobId: row.jobId,
      libraryMusicTrackIndex: row.trackIndex,
      libraryMusicTitle: `${row.songTitle} — ${row.trackTitle}`,
      referenceAudioUploads: [
        {
          id: `lib-${row.key}`,
          libraryKey: row.key,
          previewUrl,
          name: row.trackTitle,
          fromLibrary: true,
        },
      ],
      sound: true,
    });
  };

  const clearLibrary = () => {
    set({
      referenceAudioUploads: [],
      libraryMusicJobId: null,
      libraryMusicTrackIndex: null,
      libraryMusicTitle: "",
    });
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        {lang?.videoStudioMusicTrack || "Music track"}
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={musicSource}
        onChange={(_, v) => {
          if (!v) return;
          set({
            musicSource: v,
            referenceAudioUploads: [],
            libraryMusicJobId: null,
            libraryMusicTrackIndex: null,
            libraryMusicTitle: "",
          });
        }}
        sx={{ flexWrap: "wrap" }}
      >
        <ToggleButton value="library">
          {lang?.videoStudioMusicFromLibrary || "My music"}
        </ToggleButton>
        <ToggleButton value="upload">
          {lang?.videoStudioMusicUploadTab || "Upload file"}
        </ToggleButton>
      </ToggleButtonGroup>

      {musicSource === "library" ? (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
            {lang?.videoStudioMusicUpload || "Music audio"}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {libraryItem?.previewUrl || libraryLabel ? (
              <LibraryTrackTile
                label={libraryLabel}
                previewUrl={libraryItem?.previewUrl}
                onRemove={clearLibrary}
                lang={lang}
              />
            ) : (
              <MediaAddTile lang={lang} onClick={() => setLibraryOpen(true)} />
            )}
          </Stack>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
            {lang?.videoStudioMusicLibraryHint ||
              "Pick a completed track from Music Studio. Preview before selecting."}
          </Typography>
        </Box>
      ) : (
        <UploadArea
          lang={lang}
          kind="audio"
          accept="audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/x-m4a"
          maxBytes={50 * 1024 * 1024}
          label={lang?.videoStudioMusicUpload || "Music audio"}
          hint={
            lang?.videoStudioMusicUploadHint ||
            "MP3, WAV, or M4A — up to 50 MB. The video will be generated to match this track."
          }
          files={form.referenceAudioUploads || []}
          onChange={(referenceAudioUploads) =>
            set({ referenceAudioUploads, musicSource: "upload", sound: true })
          }
          maxFiles={1}
        />
      )}

      <PickMusicLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        selectedKey={librarySelectionKey(form)}
        lang={lang}
        onConfirm={selectLibraryTrack}
      />
    </Stack>
  );
}
